// src/features/reporting/pages/ReportListPage.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CardActionArea,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  Hub as HubIcon,
  Refresh as RefreshIcon,
  ShowChart as ShowChartIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { useReports } from '../hooks/useReports';
import { usePermissions } from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';

// Interactive inventory analyses; each is listed only with the scope its data needs
const ANALYSES = [
  { key: 'stock_value', path: '/reports/stock-value', icon: ShowChartIcon, scope: 'inventory:cost:read' },
  { key: 'abc', path: '/reports/abc', icon: SortIcon, scope: 'inventory:cost:read' },
  { key: 'co_removal', path: '/reports/co-removal', icon: HubIcon, scope: 'inventory:stock:read' },
];

function AnalysisCards() {
  const { t } = useTranslation(['reporting']);
  const navigate = useNavigate();
  const { hasScope } = usePermissions();
  const analyses = ANALYSES.filter((analysis) => hasScope(analysis.scope));
  if (analyses.length === 0) return null;

  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>{t('reporting:page.analyses_title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('reporting:page.analyses_subtitle')}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
        {analyses.map(({ key, path, icon: Icon }) => (
          <Paper key={key} sx={{ overflow: 'hidden' }}>
            <CardActionArea onClick={() => navigate(path)} sx={{ p: 4, display: 'flex', alignItems: 'flex-start', gap: 3, height: '100%' }}>
              <Icon sx={{ color: 'primary.main', fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="medium">{t(`reporting:${key}.title`)}</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>{t(`reporting:${key}.card`)}</Typography>
              </Box>
              <ChevronRightIcon sx={{ color: 'text.secondary', alignSelf: 'center' }} />
            </CardActionArea>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export default function ReportListPage() {
  const { t } = useTranslation(['reporting', 'common']);
  const {
    reportTypes,
    loading,
    error,
    clearError,
    generateLoading,
    generateError,
    clearGenerateError,
    generatingType,
    downloadReport,
    refresh,
  } = useReports();
  // Report names come from the server in one language; translate the known ones
  const reportName = (report) => t(`reporting:types.${report.report_type}.name`, { defaultValue: report.display_name });

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      {/* Header */}
      <PageHeader
        icon={SummarizeOutlinedIcon}
        title={t('reporting:page.title')}
        subtitle={t('reporting:page.subtitle')}
        actions={<>
          <Tooltip title={t('common:buttons.refresh')}>
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </>}
      />

      {/* Error alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      {generateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearGenerateError}>
          {generateError}
        </Alert>
      )}

      <AnalysisCards />

      {/* Report type list */}
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>{t('reporting:page.pdf_title')}</Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : reportTypes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('reporting:page.no_reports')}</Typography>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {reportTypes.map((report) => (
            <Paper
              key={report.report_type}
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {reportName(report)}
                </Typography>
                {report.description && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {t(`reporting:types.${report.report_type}.description`, { defaultValue: report.description })}
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                startIcon={
                  generatingType === report.report_type ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={generateLoading}
                onClick={() => downloadReport(report.report_type, reportName(report))}
              >
                {t('reporting:page.download')}
              </Button>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
